import React, { useState, useEffect, useCallback } from 'react';
import { useAuthContext } from '../hooks/useAuthContext';
import { useLogout } from '../hooks/useLogout';
import './SessionWarningModal.css';
import { AUTH_SESSION_DAYS } from '../authSessionConfig';

const SessionWarningModal = () => {
    const { user } = useAuthContext();
    const { logout } = useLogout();
    const [showWarning, setShowWarning] = useState(false);
    const [timeRemaining, setTimeRemaining] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const handleAutoLogout = useCallback(() => {
        // Preserve current page for redirect after login
        const currentPath = window.location.pathname;
        if (currentPath !== '/login' && currentPath !== '/signup') {
            sessionStorage.setItem('redirectAfterLogin', currentPath);
        }
        
        logout();
        setShowWarning(false);
    }, [logout]);

    useEffect(() => {
        if (!user || !user.loginTime || !user.tokenExpiresIn) return;

        const checkSessionExpiration = () => {
            const now = Date.now();
            const expirationTime = user.loginTime + user.tokenExpiresIn;
            const warningTime = expirationTime - (10 * 60 * 1000); // 10 minutes before
            const timeUntilExpiration = expirationTime - now;

            // Show warning 10 minutes before expiration
            if (now >= warningTime && now < expirationTime && !showWarning) {
                setShowWarning(true);
            }

            // Auto-logout when expired
            if (now >= expirationTime) {
                handleAutoLogout();
                return;
            }

            // Update time remaining display
            if (showWarning) {
                const minutes = Math.floor(timeUntilExpiration / (60 * 1000));
                const seconds = Math.floor((timeUntilExpiration % (60 * 1000)) / 1000);
                setTimeRemaining(`${minutes}:${seconds.toString().padStart(2, '0')}`);
            }
        };

        const interval = setInterval(checkSessionExpiration, 1000); // Check every second
        checkSessionExpiration(); // Check immediately

        return () => clearInterval(interval);
    }, [user, showWarning, handleAutoLogout]);

    const handleExtendSession = async () => {
        if (!user || !user.email) return;

        setIsLoading(true);
        try {
            // Get stored credentials for silent refresh
            const storedUser = JSON.parse(localStorage.getItem('user'));
            if (!storedUser || !storedUser.email) {
                handleAutoLogout();
                return;
            }

            // Create a new session by calling login with stored credentials
            // Note: This requires the user's password to be stored, which is not secure
            // For now, we'll just redirect to login
            handleAutoLogout();
            
        } catch (error) {
            console.error('Failed to extend session:', error);
            handleAutoLogout();
        } finally {
            setIsLoading(false);
        }
    };

    const handleLogoutNow = () => {
        handleAutoLogout();
    };

    if (!showWarning) return null;

    return (
        <div className="session-warning-overlay">
            <div className="session-warning-modal">
                <div className="session-warning-header">
                    <h3>Session Expiring Soon</h3>
                    <button 
                        className="close-button" 
                        onClick={() => setShowWarning(false)}
                        aria-label="Close warning"
                    >
                        ×
                    </button>
                </div>
                
                <div className="session-warning-content">
                    <p>Your session will expire in <strong>{timeRemaining}</strong></p>
                    <p>To continue using MyMediaTracker, please choose an option below:</p>
                </div>
                
                <div className="session-warning-actions">
                    <button 
                        className="extend-session-button"
                        onClick={handleExtendSession}
                        disabled={isLoading}
                    >
                        {isLoading ? 'Extending...' : 'Log In Again'}
                    </button>
                    
                    <button 
                        className="logout-button"
                        onClick={handleLogoutNow}
                    >
                        Log Out Now
                    </button>
                </div>
                
                <div className="session-warning-footer">
                    <small>
                        For security, sessions automatically expire after {AUTH_SESSION_DAYS} days.
                    </small>
                </div>
            </div>
        </div>
    );
};

export default SessionWarningModal;
